import { Button } from '@approvalmax/ui';
import { Box } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { LineItemsGroupEdit } from 'pages/requestForm/components';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode, RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { memo, useMemo } from 'react';
import { useNetSuiteJournalEntryFieldSettings } from 'shared/hooks';

import { SectionContainer } from '../../../../components/SectionContainer';
import { checkJournalEntryLineIsEmpty } from '../../helpers';
import {
    useCurrencyPrecision,
    useGroupEditItems,
    useLineCustomFields,
    useNetSuiteJournalEntryLines,
} from '../../hooks';
import {
    netSuiteJournalEntryFieldValuesState,
    netSuiteJournalEntryItemListState,
} from '../../NetSuiteJournalEntryRequestForm/NetSuiteJournalEntryRequestForm.states';
import { GroupActions } from '../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { netSuiteShowErrorsState } from '../../resources/states';
import { getColumnDefinitions } from './NetSuiteJournalEntryLines.config';
import { getGroupEditItemsAvailableFields } from './NetSuiteJournalEntryLines.helpers';
import { messages } from './NetSuiteJournalEntryLines.messages';
import { JournalEntryLinesTable, SectionHeader } from './NetSuiteJournalEntryLines.styles';
import { NetSuiteJournalEntryLinesProps } from './NetSuiteJournalEntryLines.types';

export const NetSuiteJournalEntryLines = memo((props: NetSuiteJournalEntryLinesProps) => {
    const { workflowId } = props;

    const editingContext = useSelector(getNetSuiteContext);
    const showErrors = useAtomValue(netSuiteShowErrorsState);
    const journalEntryItemList = useAtomValue(netSuiteJournalEntryItemListState);
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const isApproverMode = editMode === RequestEditMode.Approver;

    const { currency } = useAtomValue(netSuiteJournalEntryFieldValuesState);

    const currencyPrecision = useCurrencyPrecision(currency);

    const { accounts, classes, departments, locations, customFields, documentFields } = editingContext;

    const fieldSettings = useNetSuiteJournalEntryFieldSettings(documentFields);

    const availableFields = useMemo(() => getGroupEditItemsAvailableFields(fieldSettings), [fieldSettings]);

    const showErrorsOnEmptyLine = useMemo(() => {
        return journalEntryItemList.every(checkJournalEntryLineIsEmpty);
    }, [journalEntryItemList]);

    const {
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateLineItem,
        updateCustomField,
        onAddLine,
        onReorderLines,
        onRemoveLine,
        onCloneLine,
    } = useNetSuiteJournalEntryLines();

    const { availableCustomFields, customFieldsColumnDefinition } = useLineCustomFields({
        customFields,
        workflowId,
        sublistType: domain.DocumentSublistType.ItemList,
        showErrors,
        showErrorsOnEmptyLine,
        updateCustomField,
    });

    const {
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isShowGroupButtons,
        groupItems,
        openGroupEdit,
        handleGroupClone,
        handleGroupRemove,
        closeGroupEdit,
    } = useGroupEditItems({
        classes,
        departments,
        locations,
        workflowId,
        availableFields,
        availableCustomFields,
        items: journalEntryItemList,
        onRemoveLine,
        onCloneLine,
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateCustomField,
    });

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                accounts,
                showErrors,
                showErrorsOnEmptyLine,
                fieldSettings,
                classes,
                departments,
                locations,
                isApproverMode,
                currencyPrecision,
                customFieldsColumnDefinition,
                onFieldUpdate,
                onMultiLineFieldUpdate,
                updateLineItem,
            }),
        [
            accounts,
            showErrors,
            showErrorsOnEmptyLine,
            fieldSettings,
            classes,
            departments,
            locations,
            isApproverMode,
            currencyPrecision,
            customFieldsColumnDefinition,
            onFieldUpdate,
            onMultiLineFieldUpdate,
            updateLineItem,
        ]
    );

    return (
        <SectionContainer
            title={
                <SectionHeader>
                    <Box spacing='0 20'>{messages.sectionName}</Box>

                    {isShowGroupButtons && (
                        <GroupActions>
                            {isGroupEditVisible && (
                                <Button preset='small' execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </GroupActions>
                    )}
                </SectionHeader>
            }
            noPadding
        >
            <JournalEntryLinesTable
                columnDefinitions={columnDefinitions}
                lineItems={journalEntryItemList}
                invalid={false}
                onReorder={onReorderLines}
                onRemoveLine={onRemoveLine}
                onCloneLine={onCloneLine}
                onAddLine={onAddLine}
            />

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </SectionContainer>
    );
});

NetSuiteJournalEntryLines.displayName = 'NetSuiteJournalEntryLines';
